// Which screen a page load shows first (ADR-WBC1). Shared by the head
// bootstrap (src/boot/head.ts, which runs before the bundle and preloads that
// screen's files) and App.tsx (which renders it), so the two never disagree.
// Dependency-free on purpose: the head script bundles this file.
//
// The stored-session hint only CHOOSES which screen to try first. It never
// authorizes anything: the console still waits for Supabase to return a real
// session, and the API checks every request itself.

export type BootScreen = "splash" | "auth" | "dashboard" | "api" | "docs";

export type AuthModeFromUrl = "login" | "signup" | "forgot" | "reset" | "invite";

/** The parts of `window.location` the decision reads. */
export interface BootLocation {
  pathname: string;
  search: string;
  hash: string;
}

function hashParams(location: BootLocation): URLSearchParams {
  return new URLSearchParams(location.hash.replace(/^#/, ""));
}

/**
 * The auth mode a link asks for. `authMode` is the legacy alias of `mode`;
 * Supabase email links carry `type=invite|recovery` in the query (PKCE) or the
 * hash (implicit flow).
 */
export function authModeFromUrl(location: BootLocation): AuthModeFromUrl | null {
  const params = new URLSearchParams(location.search);
  const mode = params.get("mode") ?? params.get("authMode");
  if (mode === "login" || mode === "signup" || mode === "forgot" || mode === "reset" || mode === "invite") {
    return mode;
  }
  if (params.get("type") === "invite") return "invite";
  if (params.get("type") === "recovery") return "reset";
  const hash = hashParams(location);
  if (hash.get("type") === "invite") return "invite";
  return hash.get("type") === "recovery" ? "reset" : null;
}

/**
 * True when the URL is an auth callback (PKCE `code`, implicit tokens in the
 * hash) or a password-setup step (recovery or invite). Those flows must finish
 * the auth check before anything renders, exactly as before ADR-WBC1.
 */
export function urlRequiresAuthCheck(location: BootLocation): boolean {
  const params = new URLSearchParams(location.search);
  const hash = hashParams(location);
  if (params.has("code") || hash.has("access_token") || hash.has("refresh_token")) return true;
  const mode = authModeFromUrl(location);
  return mode === "reset" || mode === "invite";
}

/** The screen the URL asks for, before any session is known. */
export function requestedScreen(location: BootLocation): BootScreen {
  if (location.pathname.startsWith("/docs")) return "docs";
  const mode = authModeFromUrl(location);
  if (mode === "reset" || mode === "invite") return "auth";
  const params = new URLSearchParams(location.search);
  const requested = params.get("screen");
  if (requested === "dashboard" || requested === "api" || requested === "auth" || requested === "docs") {
    return requested;
  }
  if (params.get("dashboard") === "1") return "dashboard";
  return "splash";
}

export interface BootPlan {
  /** The screen to render first. */
  screen: BootScreen;
  /**
   * True when the stored-session hint, not the URL, chose the console: the
   * site root for a visitor who appears signed in (founder, 2026-09-30). If
   * the auth check then finds no session, the app returns to the splash.
   */
  promotedBySession: boolean;
  /** Whether the first screen waits for the auth check (screenWaitsForAuth). */
  waitsForAuth: boolean;
}

/**
 * Whether a screen waits for the auth check before it renders. Public screens
 * do not. The console, auth callbacks and password setup do. So do the screens
 * that show a signed-in visitor something else once the session is known (the
 * sign-in form redirects them to the console; the partner application form
 * shows their existing application), but only when a session is probably
 * stored, so that a visitor without one never waits. App.tsx applies this to
 * the current screen on every render.
 */
export function screenWaitsForAuth(
  screen: BootScreen,
  { authCallback, sessionHint }: { authCallback: boolean; sessionHint: boolean }
): boolean {
  if (authCallback || screen === "dashboard") return true;
  return sessionHint && (screen === "auth" || screen === "api");
}

export function bootPlan(location: BootLocation, hasStoredSession: boolean): BootPlan {
  const requested = requestedScreen(location);
  const authCallback = urlRequiresAuthCheck(location);
  if (authCallback) {
    return { screen: requested, promotedBySession: false, waitsForAuth: true };
  }
  const params = new URLSearchParams(location.search);
  const siteRoot = location.pathname === "/" && !params.has("screen") && !params.has("dashboard");
  if (hasStoredSession && requested === "splash" && siteRoot) {
    return { screen: "dashboard", promotedBySession: true, waitsForAuth: true };
  }
  const waitsForAuth = screenWaitsForAuth(requested, { authCallback, sessionHint: hasStoredSession });
  return { screen: requested, promotedBySession: false, waitsForAuth };
}
